import './styles.scoped.css'
import {useEffect} from 'react'
import {Typography, Button, IconButton, FormControl, FormHelperText, Modal, TextField, Select, MenuItem} from '@mui/material'
import {Close} from '@mui/icons-material'
import {Controller, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {useMobile} from '../../../../hooks'
import {usStates} from '../../../../utils/helpers'
import {AddressAutocompleteField} from '../../../../components'

export default ({open, setOpen, clientAddress, setClientAddress}) => {
    const {mobile} = useMobile()
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                street_line1: yup.string().required('Address Line 1 is required'),
                street_line2: yup.string(),
                city: yup.string().required('City is required'),
                state: yup.string().required('State is required'),
                zip_code: yup
                    .string()
                    .required('Zip Code is required')
                    .min(5, 'Zip Code must be between 5 and 20 characters')
                    .max(20, 'Zip Code must be between 5 and 20 characters')
                    .matches(/^\d+$/, 'Zip Code must contain only numbers'),
                notes: yup.string(),
                latitude: yup
                    .number()
                    .nullable()
                    .transform((value, originalValue) => (originalValue === '' ? null : value)),
                longitude: yup
                    .number()
                    .nullable()
                    .transform((value, originalValue) => (originalValue === '' ? null : value)),
            }),
        ),
    })
    useEffect(() => {
        if (clientAddress) {
            form.setValue('street_line1', clientAddress?.street_line1)
            form.setValue('street_line2', clientAddress?.street_line2)
            form.setValue('city', clientAddress?.city)
            form.setValue('state', clientAddress?.state)
            form.setValue('zip_code', clientAddress?.zip_code)
            form.setValue('notes', clientAddress?.notes)
            form.setValue('latitude', clientAddress?.latitude)
            form.setValue('longitude', clientAddress?.longitude)
        }
    }, [clientAddress])

    const selectAddress = selected => {
        form.setValue('street_line2', '')
        form.setValue('city', selected?.city)
        form.setValue('state', selected?.state)
        form.setValue('zip_code', selected?.postcode)
        form.setValue('latitude', selected?.lat)
        form.setValue('longitude', selected?.lon)
    }

    const submit = data => {
        setClientAddress(prev => ({...prev, ...data}))
        setOpen(false)
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    {mobile ? (
                        <>
                            <div className="header">
                                <Typography variant="h6" color="text.primary">
                                    Update Address Details
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                            <div className="actions">
                                <Button variant="outlined" onClick={() => setOpen(false)} style={{flex: 1}}>
                                    Back
                                </Button>
                                <Button variant="contained" fullWidth onClick={form.handleSubmit(submit)} style={{flex: 1}}>
                                    Save
                                </Button>
                            </div>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Typography variant="h6" color="text.primary" className="title">
                            Update Address Details
                        </Typography>
                    )}
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Street Address Line 1{' '}
                        <Typography variant="inputLabel" color="error">
                            *
                        </Typography>
                    </Typography>
                    <AddressAutocompleteField
                        control={form.control}
                        name="street_line1"
                        error={!!form.formState.errors['street_line1']}
                        helperText={form.formState.errors['street_line1']?.message}
                        onSelect={selectAddress}
                    />
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Street Address Line 2
                    </Typography>
                    <TextField placeholder="Enter your street address line 2" fullWidth {...form.register('street_line2')} />
                    <br />
                    <br />
                    <div className="form">
                        <div className="form-left">
                            <Typography variant="inputLabel" color="text.secondary">
                                City{' '}
                                <Typography variant="inputLabel" color="error">
                                    *
                                </Typography>
                            </Typography>
                            <TextField
                                placeholder="Enter your city"
                                fullWidth
                                {...form.register('city')}
                                error={!!form.formState.errors['city']}
                                helperText={form.formState.errors['city']?.message}
                            />
                        </div>
                        <div className="form-right">
                            <Typography variant="inputLabel" color="text.secondary">
                                State{' '}
                                <Typography variant="inputLabel" color="error">
                                    *
                                </Typography>
                            </Typography>
                            <FormControl fullWidth error={!!form.formState.errors['state']}>
                                <Controller
                                    {...form.register('state')}
                                    control={form.control}
                                    render={({field}) => (
                                        <Select
                                            {...field}
                                            value={field.value ?? ''}
                                            onChange={e => field.onChange(e.target.value)}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300,
                                                    },
                                                },
                                                anchorOrigin: {
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                },
                                                transformOrigin: {
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                },
                                            }}
                                            displayEmpty
                                            renderValue={selected => {
                                                if (selected === '') {
                                                    return <span style={{color: '#aaa'}}>Select your state</span>
                                                }
                                                return selected
                                            }}>
                                            {usStates.map(item => (
                                                <MenuItem key={item} value={item}>
                                                    {item}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                <FormHelperText>{form.formState.errors['state']?.message}</FormHelperText>
                            </FormControl>
                        </div>
                    </div>
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Zip Code{' '}
                        <Typography variant="inputLabel" color="error">
                            *
                        </Typography>
                    </Typography>
                    <TextField
                        placeholder="Enter your zip code"
                        fullWidth
                        {...form.register('zip_code')}
                        error={!!form.formState.errors['zip_code']}
                        helperText={form.formState.errors['zip_code']?.message}
                    />
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Additional Notes
                    </Typography>
                    <TextField placeholder="Add additional notes..." fullWidth {...form.register('notes')} />
                    <br />
                    <br />
                    {mobile ? (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <div className="actions">
                            <Button onClick={() => setOpen(false)} variant="outlined" fullWidth>
                                Cancel
                            </Button>
                            <Button variant="contained" fullWidth onClick={form.handleSubmit(submit)}>
                                Save
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
